/**
 * eslint-disable @typescript-eslint/naming-convention
 *
 * @format
 */

/**
 *
 * @format
 */

import {
	Chain,
	CriteoPlacement,
	CriteoProduct,
	CriteoTrackTransactionResponse,
	CriteoTransactionMetadata,
	CriteoViewItemMetadata,
	CriteoViewItemResponse,
	CriteoViewProductsMetadata,
	CriteoViewProductsResponse,
	Environment,
	PlpInGridSponsoredProduct,
	ProductSku,
	ProductTypes
} from '@shared/data-access/interfaces';
import { EMPTY, Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';

import { CertonaSchemeItem } from '@product-recommendations/data-access';
import { Injectable } from '@angular/core';
import { Logger } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class CriteoService {
	private readonly CRITEO_API_URL = '';
	private readonly validFilterKeys: Record<string, string> = {
		'299': 'size', //size
		'5225': 'shoe size', //shoe size
		'5212': 'mens shoe size', //mens shoe size
		'5213': 'womens shoe size', //womens shoe size
		'5214': 'kids shoe size', //kids shoe size
		X_BRAND: 'brand', //brand
		'3232': 'color', //color
		dickssportinggoodsofferprice: 'price_range', //price
		golfgalaxyofferprice: 'price_range' //price
	};
	lastInGridOnLoadBeacon = ''; //This is used for the inGrid onLoadBeacons. This is to prevent two criteo calls to be made

	// PLP/SRLP Global variables
	everyThirdSlot = 0;
	pinnedContentInFirstRow = 0;
	sponsoredShelfPositions: number[] = [4, 5, 6, 7];
	shelfChecked = false;

	constructor(
		private readonly http: HttpClient,
		private readonly environment: Environment,
		private readonly chain: Chain,
		private readonly logger: Logger
	) {
		this.CRITEO_API_URL = this.environment.api.criteo.baseUrl;
	}

	public parseFilters(filters: Record<string, string[]>): string {
		const filterEntries = Object.entries(filters);

		if (filterEntries.length === 0) {
			return '';
		}

		const filterStrings = filterEntries
			.filter(([key]) => this.validFilterKeys[key])
			.map(
				([key, values]) =>
					`(${this.validFilterKeys[key]},${this.formatFilterValues(values)})`
			);

		return filterStrings.join(',');
	}

	private formatFilterValues(values: string[]): string {
		const formattedValues = values.map((value) => {
			if (value.startsWith('*-')) {
				return `Under ${value.slice(2)}`;
			}
			if (value.endsWith('-*')) {
				return `Over ${value.slice(0, -2)}`;
			}
			return value;
		});
		if (formattedValues.length === 1) {
			return `eq,${formattedValues[0]}`;
		}
		return `in,${formattedValues.join(',')}`;
	}

	public transformToCriteoBreadcrumbFormat(input: string): string {
		const parts = input.split('_');
		const transformedParts = parts.slice(1);
		return transformedParts.join('>');
	}

	private getHeaders() {
		const httpHeaders = {
			['X-TIMEOUT']: '10000'
		};
		return new HttpHeaders(httpHeaders);
	}

	private getDefaultParams(eventType: string) {
		const isLoggedInStr = window.localStorage.getItem('TAG_AuthState');
		const isLoggedIn = isLoggedInStr === 'true';
		let params = new HttpParams()
			.set(
				'criteo-partner-id',
				this.environment.api.criteo.partnerId[this.chain.chainIdentifierAbbr]
			)
			.set('retailer-visitor-id', window._dsgTag?.CustomerIDs?.AdobeMCVID)
			.set('environment', window._dsgTag?.experience === 'Mobile' ? 'm' : 'd')
			.set('nolog', this.environment.api.criteo.nolog)
			.set('event-type', eventType)
			.set('page-id', `${eventType}_API_${window._dsgTag?.experience?.toLowerCase()}`);
		if (isLoggedIn) {
			params = params
				.set('email', window._dsgTag?.CustomerIDs?.HashedEmail)
				.set('customer-id', window._dsgTag?.CustomerIDs?.AthleteID);
		}
		return params;
	}

	private isOptedOut(): boolean {
		if (window._dsgTag?.DoNotSell) {
			console.warn('Athlete opted out, not sending request to Criteo.');
			return true;
		}
		return false;
	}

	private handleError(error: any): Observable<never> {
		this.logger.error('Criteo call failed: ', error);
		return EMPTY;
	}

	public trackTransaction(
		transactionMetadata: CriteoTransactionMetadata
	): Observable<CriteoTrackTransactionResponse> {
		if (this.isOptedOut()) {
			return EMPTY;
		}

		const headers = this.getHeaders();
		const defaultParams = this.getDefaultParams('trackTransaction');
		let httpParams = defaultParams;
		httpParams = this.addParamIfPresent(httpParams, 'item', transactionMetadata.items); // '123|456|789'
		httpParams = this.addParamIfPresent(httpParams, 'price', transactionMetadata.prices); // '2.99|23.99|54.49'
		httpParams = this.addParamIfPresent(httpParams, 'quantity', transactionMetadata.quantities); // '1|4|1'
		httpParams = this.addParamIfPresent(
			httpParams,
			'transaction-id',
			transactionMetadata.transactionId
		);
		httpParams = this.addParamIfPresent(httpParams, 'currency', transactionMetadata.currency);

		return this.http
			.get<CriteoTrackTransactionResponse>(this.CRITEO_API_URL, {
				headers,
				params: httpParams
			})
			.pipe(catchError(this.handleError.bind(this)));
	}

	public viewItem(viewItemMetadata: CriteoViewItemMetadata): Observable<CriteoViewItemResponse> {
		if (this.isOptedOut()) {
			return EMPTY;
		}

		const headers = this.getHeaders();
		const defaultParams = this.getDefaultParams('viewItem');
		let httpParams = defaultParams;
		httpParams = this.addParamIfPresent(httpParams, 'item', viewItemMetadata.item);
		httpParams = this.addParamIfPresent(httpParams, 'parent-item', viewItemMetadata.parentItem);
		httpParams = this.addParamIfPresent(httpParams, 'category', viewItemMetadata.category);
		httpParams = this.addParamIfPresent(httpParams, 'price', viewItemMetadata.price);
		httpParams = this.addParamIfPresent(
			httpParams,
			'availability',
			viewItemMetadata.availability
		);

		return this.http
			.get<CriteoViewItemResponse>(this.CRITEO_API_URL, { headers, params: httpParams })
			.pipe(
				map((response: CriteoViewItemResponse) => {
					const placements = response.placements.map((placement) => {
						const carousel = Object.values(placement).flat();
						return { carousel };
					});

					if (placements.length === 0) {
						placements.push({ carousel: [{ products: [] } as CriteoPlacement] });
					}

					const res: CriteoViewItemResponse = {
						OnLoadBeacon: response.OnLoadBeacon,
						placements
					};
					return res;
				}),
				catchError(this.handleError.bind(this))
			);
	}

	private addParamIfPresent(
		params: HttpParams,
		key: string,
		value: string | number | boolean | undefined
	): HttpParams {
		if (value !== null && value !== undefined && value !== '') {
			return params.set(key, value.toString());
		}
		return params;
	}

	public viewProductsPage(
		viewProductsMetadata: CriteoViewProductsMetadata
	): Observable<CriteoViewProductsResponse> {
		if (this.isOptedOut()) return EMPTY;

		const headers = this.getHeaders();
		const defaultParams = this.getDefaultParams(
			ProductTypes.VIEW_CATEGORY == viewProductsMetadata.productType
				? 'viewCategory'
				: 'viewSearchResult'
		);

		let httpParams = defaultParams;
		httpParams = this.addParamIfPresent(httpParams, 'list-size', viewProductsMetadata.listSize);
		httpParams = this.addParamIfPresent(
			httpParams,
			'page-number',
			viewProductsMetadata.pageNumber + 1 // Criteo API is 1-indexed
		);
		httpParams = this.addParamIfPresent(httpParams, 'filters', viewProductsMetadata.filters);
		httpParams = this.addParamIfPresent(httpParams, 'item', viewProductsMetadata.item);
		httpParams = this.addParamIfPresent(
			httpParams,
			'parent-item',
			viewProductsMetadata.parentItem
		);

		if (viewProductsMetadata.productType == ProductTypes.VIEW_CATEGORY) {
			httpParams = this.addParamIfPresent(
				httpParams,
				'category',
				viewProductsMetadata.category
			);
		} else {
			httpParams = this.addParamIfPresent(
				httpParams,
				'keywords',
				viewProductsMetadata.category
			);
		}

		return this.http
			.get<CriteoViewProductsResponse>(this.CRITEO_API_URL, { headers, params: httpParams })
			.pipe(
				map((response: CriteoViewProductsResponse) => {
					const carousel = [];
					const inGrid = [];

					response.placements.forEach((placement) => {
						const [key, value] = Object.entries(placement)[0];
						if (key.toLowerCase().includes('carousel')) {
							carousel.push(value[0]);
						} else if (key.toLowerCase().includes('ingrid')) {
							inGrid.push(value[0]);
						}
					});

					if (carousel.length === 0) {
						carousel.push({ products: [] } as CriteoPlacement);
					}
					if (inGrid.length === 0) {
						inGrid.push({ products: [] } as CriteoPlacement);
					}
					const res: CriteoViewProductsResponse = {
						OnLoadBeacon: response.OnLoadBeacon,
						placements: [{ carousel }, { inGrid }]
					};

					return res;
				}),
				tap({
					error: (error) => {
						this.logger.error('Criteo call failed: ', error);
					}
				})
			);
	}

	/*
		@param
			beaconUrl - Expect unmodified url from criteo
			excluded - optional, expects productIds separated by pipe
				Ex. 123|456|098
	*/
	public fireBeacon(beaconUrl: string, excluded?: string): void {
		if (!beaconUrl) return;
		if (excluded) {
			beaconUrl = beaconUrl.concat(`&ex=${excluded}`);
		}
		if (navigator.sendBeacon) {
			navigator.sendBeacon(beaconUrl);
		} else {
			const img = new Image();
			img.src = beaconUrl;
		}
	}

	public transformCriteoProducts(
		sponsoredProducts: CriteoProduct[],
		forCertona = false
	): CertonaSchemeItem[] | PlpInGridSponsoredProduct[] {
		const criteoDisplayables: (CertonaSchemeItem | PlpInGridSponsoredProduct)[] = [];
		sponsoredProducts.forEach((product) => {
			const formattedProduct = forCertona
				? this.getFormattedProductForCarousel(product)
				: this.getFormattedProductForPLP(product);

			try {
				const renderingAttr = JSON.parse(product.RenderingAttributes);
				const renderingAttributesString = renderingAttr.rendering_attributes;
				const renderingAttributesPrices =
					renderingAttributesString.match(/Price:\[(.*?)\]/)?.[1];

				if (renderingAttributesPrices) {
					const jsonString = `{${renderingAttributesPrices.replace(/'/g, '"')}}`;
					const priceObject = JSON.parse(jsonString);

					if (forCertona) {
						const certonaProduct = formattedProduct as CertonaSchemeItem;
						certonaProduct.mapPriceIndicator =
							priceObject?.mapPriceIndicator ?? certonaProduct.mapPriceIndicator;
						certonaProduct.maxlistprice =
							priceObject?.maxlistprice ?? certonaProduct.maxlistprice;
						certonaProduct.maxmapprice =
							priceObject?.maxmapprice ?? certonaProduct.maxmapprice;
						certonaProduct.maxofferprice =
							priceObject?.maxofferprice ?? certonaProduct.maxofferprice;
						certonaProduct.minlistprice =
							priceObject?.minlistprice ?? certonaProduct.minlistprice;
						certonaProduct.minmapprice =
							priceObject?.minmapprice ?? certonaProduct.minmapprice;
						certonaProduct.minofferprice =
							priceObject?.minofferprice ?? certonaProduct.minofferprice;
					} else {
						const plpProduct = formattedProduct as PlpInGridSponsoredProduct;
						plpProduct.product.mapPriceIndicator =
							priceObject?.mapPriceIndicator ?? plpProduct.product.mapPriceIndicator;
						plpProduct.product.maxlistprice =
							priceObject?.maxlistprice ?? plpProduct.product.maxlistprice;
						plpProduct.product.maxmapprice =
							priceObject?.maxmapprice ?? plpProduct.product.maxmapprice;
						plpProduct.product.maxofferprice =
							priceObject?.maxofferprice ?? plpProduct.product.maxofferprice;
						plpProduct.product.minlistprice =
							priceObject?.minlistprice ?? plpProduct.product.minlistprice;
						plpProduct.product.minmapprice =
							priceObject?.minmapprice ?? plpProduct.product.minmapprice;
						plpProduct.product.minofferprice =
							priceObject?.minofferprice ?? plpProduct.product.minofferprice;

						const renderingAttributesColors: string[] = renderingAttributesString
							.match(/Color:\[(.*?)\]/)?.[1]
							?.match(/"([^"]+)"/g)
							?.map((color) => color.replace(/"/g, ''));
						if (renderingAttributesColors) {
							plpProduct.product.Color = renderingAttributesColors;
						}
					}
				}
			} catch (error) {
				this.logger.error('Error parsing rendering attributes: ', {
					error,
					renderingAttr: product.RenderingAttributes
				});
			}
			criteoDisplayables.push(formattedProduct);
		});

		return forCertona
			? (criteoDisplayables as CertonaSchemeItem[])
			: (criteoDisplayables as PlpInGridSponsoredProduct[]);
	}

	private getFormattedProductForPLP(product: CriteoProduct): PlpInGridSponsoredProduct {
		return {
			click_tracker: product.OnClickBeacon,
			impression_tracker: product.OnViewBeacon,
			onBasketChangeBeacon: product.OnBasketChangeBeacon,
			onWishlistBeacon: product.OnWishlistBeacon,
			isCriteo: true,
			sponsored: true,
			product_id: product.ParentSKU,
			product: {
				Color: [''],
				CustomerRating: product.Rating,
				ID: product.ProductId,
				ImageURL: product.Image,
				Name: product.ProductName,
				mapPriceIndicator: 0,
				maxlistprice: '',
				maxmapprice: '',
				maxofferprice: '',
				minlistprice: '',
				minmapprice: '',
				minofferprice: '',
				offerprice: '',
				onlineInventoryMessage: ''
			}
		};
	}

	private getFormattedProductForCarousel(product: CriteoProduct): CertonaSchemeItem {
		return {
			click_tracker: product.OnClickBeacon,
			impression_tracker: product.OnViewBeacon,
			BreakingMap: '',
			CustomerRating: product.Rating,
			ID: product.ProductId,
			ImageURL: product.Image,
			Name: product.ProductName,
			ReviewCount: '',
			SKUID: product.ParentSKU,
			URL: product.ProductPage,
			WCSIdentifier: '',
			isSkuRecommendation: false,
			mapPriceIndicator: 0,
			maxlistprice: '',
			maxmapprice: '',
			maxofferprice: '',
			minlistprice: '',
			minmapprice: '',
			minofferprice: '',
			offerprice: ''
		};
	}
	//PLP Functions:
	public fireInGridOnLoadBeacon(beaconURL: string, excludedProducts = ''): void {
		if (!beaconURL || beaconURL === this.lastInGridOnLoadBeacon) {
			return;
		}
		this.fireBeacon(beaconURL, excludedProducts);
		this.lastInGridOnLoadBeacon = beaconURL;
	}

	public isSponsoredProduct(
		sponsoredProducts: PlpInGridSponsoredProduct[] | null,
		productCardCount: number,
		pageNumber: number,
		filters: Record<string, string[]>,
		featureFlags: Record<string, boolean>,
		pinnedContent
	): boolean {
		// Ensure there are sponsored products available to place
		// Also ensure that if the product isn't a criteo product and we are either filtering or not on the first page, return false.
		if (
			!sponsoredProducts?.length ||
			(!sponsoredProducts[0]?.isCriteo &&
				(pageNumber !== 0 || Object.keys(filters).length !== 0))
		) {
			return false;
		}

		// Only run this if we are using the sponsored shelf and if we haven't checked yet
		if (featureFlags.isSPAShelfEnabled && !this.shelfChecked) {
			for (let i = 0; i < 4; i++) {
				if (typeof pinnedContent?.[i] !== 'undefined') {
					this.pinnedContentInFirstRow++;
				}
			}
			this.sponsoredShelfPositions = this.sponsoredShelfPositions.map(
				(pos) => pos - this.pinnedContentInFirstRow
			);
			this.everyThirdSlot = this.sponsoredShelfPositions[3] % 3;
			this.shelfChecked = true;
		}

		// If the shelf is enabled and the pCC is equal to one of the shelf position numbers, return true.
		// If the second position is enabled and we are at the second position (pCC is equal to 1), return true.
		// If we are past the 7th position (pCC >= 6), check for every third position (calculated above). If we are at a third position, return true.
		// Otherwise, return false.
		return (
			(this.sponsoredShelfPositions.includes(productCardCount) &&
				featureFlags.isSPAShelfEnabled) ||
			(productCardCount === 1 && featureFlags.isSPAPosition2Enabled) ||
			(productCardCount >= 6 && productCardCount % 3 === this.everyThirdSlot)
		);
	}

	/**
	 * Creates CriteoViewItemMetadata from a ProductSku for pdp templates
	 * @param productSku The first SKU from the product
	 * @returns CriteoViewItemMetadata object
	 */
	public createViewItemMetadata(productSku: ProductSku): CriteoViewItemMetadata {
		return {
			item: productSku?.partNumber,
			price: productSku?.prices?.offerPrice?.toString(),
			availability: productSku?.buyable,
			parentItem: productSku?.parentPartNumber
		};
	}

	public checkOnLoadBeaconFiring(
		sponsoredSEO: string,
		beaconURL: string,
		sponProductCopy: PlpInGridSponsoredProduct[] | null,
		skippedIDs: string[]
	): void {
		if (sponsoredSEO === 'N') {
			return;
		}
		// If there are any sponspored products left in the copy array, add it onto the already skipped sponsored products.
		sponProductCopy.forEach((skipped) => skippedIDs.push(skipped.product.ID));

		// Get all the SKUs for the excluded products, put them in a pipe separated string, then send it with the placement level beacon
		const excludedProductsString = skippedIDs.join('|');
		this.fireInGridOnLoadBeacon(beaconURL, excludedProductsString);
	}
}
